import * as React from "react";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { NavigateFunction } from "react-router-dom";
import { ImportSettingsDialog } from "./ImportSettingsDialog";
import { useImportSettingsStore } from "./importSettingsStore";
import {
  ImportSettingKey,
  SiteImportCapabilities,
  fetchSiteImportCapabilities,
  previewHasImportableValues,
} from "../../utils/importSettingsApi";
import { markSiteUnsynced } from "../../utils/syncStore";
import { logger } from "../../../utils/logger";

export const IMPORT_SETTINGS_NOTICE =
  "Couldn't check the VMS for settings to import. You can import ignore zones later from each camera's page.";

/**
 * End-of-auto-add card offering to import configuration that already exists
 * on the customer's VMS (ENG-2461). Renders nothing unless the site's About
 * payload reports a non-empty `importable_settings` AND the preview has at
 * least one camera with something to bring over; in every other case —
 * including any preview failure or a slow admin — it calls `onDone` so the
 * wizard advances exactly as it would have without the card. `onDone` runs
 * exactly once.
 */
export const ImportSettingsOnboardingPrompt = ({
  siteId,
  cameraIds,
  onDone,
  navigate,
  lookupTimeoutMs = 15_000,
  noticeMs = 4_000,
}: {
  siteId: string | number;
  /** The cameras auto-add just created; omitted = every camera on the site. */
  cameraIds?: number[];
  onDone: () => void;
  navigate: NavigateFunction;
  lookupTimeoutMs?: number;
  noticeMs?: number;
}) => {
  const [capabilities, setCapabilities] =
    React.useState<SiteImportCapabilities | null>(null);
  const [phase, setPhase] = React.useState<"lookup" | "dialog" | "notice">(
    "lookup"
  );
  const doneRef = React.useRef(false);
  const onDoneRef = React.useRef(onDone);
  onDoneRef.current = onDone;
  const giveUpRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const preview = useImportSettingsStore((s) => s.preview);
  const previewError = useImportSettingsStore((s) => s.previewError);
  const applying = useImportSettingsStore((s) => s.applying);
  const results = useImportSettingsStore((s) => s.results);
  const applyError = useImportSettingsStore((s) => s.applyError);
  const loadPreview = useImportSettingsStore((s) => s.loadPreview);
  const apply = useImportSettingsStore((s) => s.apply);
  const reset = useImportSettingsStore((s) => s.reset);

  const finish = React.useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    if (giveUpRef.current) clearTimeout(giveUpRef.current);
    onDoneRef.current();
  }, []);

  // Step 1: does this site's integration offer anything? Then ask for the
  // preview. Never hold up onboarding on a slow admin.
  React.useEffect(() => {
    const controller = new AbortController();
    giveUpRef.current = setTimeout(() => {
      controller.abort();
      logger.error("import_settings lookup timed out; continuing onboarding");
      finish();
    }, lookupTimeoutMs);
    reset();
    fetchSiteImportCapabilities(siteId, controller.signal).then(
      (caps) => {
        if (controller.signal.aborted) return;
        if (caps.importable_settings.length === 0) {
          finish();
          return;
        }
        setCapabilities(caps);
        void loadPreview(siteId, cameraIds, caps.importable_settings, navigate);
      },
      (reason) => {
        if (controller.signal.aborted) return;
        logger.error("import_settings capability lookup failed", reason);
        finish();
      }
    );
    return () => {
      controller.abort();
      if (giveUpRef.current) clearTimeout(giveUpRef.current);
    };
    // Runs once per site: cameraIds/navigate are stable for the flow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, finish, lookupTimeoutMs]);

  // Step 2: the preview landed (possibly after a retry inside the shared
  // error handler) or failed for good.
  React.useEffect(() => {
    if (!capabilities || phase !== "lookup") return;
    if (previewError) {
      if (giveUpRef.current) clearTimeout(giveUpRef.current);
      setPhase("notice");
      return;
    }
    if (!preview) return;
    if (giveUpRef.current) clearTimeout(giveUpRef.current);
    if (previewHasImportableValues(preview, capabilities.importable_settings)) {
      setPhase("dialog");
    } else {
      finish();
    }
  }, [capabilities, phase, preview, previewError, finish]);

  const handleImport = (settings: ImportSettingKey[]) => {
    if (!capabilities || !preview) return;
    const ids =
      cameraIds && cameraIds.length > 0
        ? cameraIds
        : preview.cameras.map((camera) => camera.id);
    void apply(siteId, ids, settings, false, navigate).then((response) => {
      if (response) {
        // Imported zones/names/enabled are connector settings: nothing reaches
        // the site until the operator syncs.
        markSiteUnsynced(String(siteId));
      }
    });
  };

  if (phase === "notice") {
    return (
      <Snackbar
        open
        autoHideDuration={noticeMs}
        onClose={finish}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="warning"
          onClose={finish}
          sx={{ fontFamily: "Mulish" }}
        >
          {IMPORT_SETTINGS_NOTICE}
        </Alert>
      </Snackbar>
    );
  }

  if (phase !== "dialog" || !capabilities || !preview) return null;
  return (
    <ImportSettingsDialog
      open
      preview={preview}
      offeredSettings={capabilities.importable_settings}
      integrationLabel={
        capabilities.integration_label || preview.integration_label || ""
      }
      applying={applying}
      results={results}
      applyError={applyError}
      onSkip={finish}
      onImport={handleImport}
      onContinue={finish}
    />
  );
};
